/**
 * Vendor portal — V0.3 self-service surface for empanelled vendors.
 *
 * Sits outside the AppShell admin chrome (no Sidebar) — vendors are an
 * external contractor identity. Three tabs:
 *   1. Active jobs   — open assignments, "Mark complete" action per card
 *   2. History       — resolved assignments with notes + (optional) rating
 *   3. Invoices      — vendor's payout history
 *
 * Realtime: subscribes to `complaint:assigned` so newly-assigned jobs slot
 * into the Active list without a manual refresh.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import {
  Wrench,
  LogOut,
  Briefcase,
  CheckCircle2,
  IndianRupee,
  AlertTriangle,
  Clock3,
  Star,
  Receipt
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { LogoMark } from '@/components/brand/Logo';
import { get, post, getErrorMessage } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { subscribe } from '@/lib/socket';
import { formatDate, formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';

// ───── types ─────

type VendorCategory =
  | 'plumbing'
  | 'electrical'
  | 'carpentry'
  | 'ac_repair'
  | 'painting'
  | 'pest_control'
  | 'housekeeping'
  | 'gardening'
  | 'lift_maintenance'
  | 'security_system'
  | 'other';

type SlaPriority = 'low' | 'normal' | 'high' | 'urgent';
type InvoiceStatus = 'unpaid' | 'paid' | 'cancelled';
type ComplaintPriority = 'low' | 'normal' | 'high' | 'urgent';
type ComplaintStatus = 'open' | 'assigned' | 'resolved' | 'closed';

interface VendorProfile {
  id: string;
  name: string;
  category: VendorCategory;
  contact_name: string | null;
  phone: string;
  email: string | null;
  status: 'active' | 'inactive' | 'blacklisted';
  rating_avg: number | null;
  jobs_completed: number;
  total_paid_inr: number;
  last_login_at: string | null;
  society_id: string;
}

interface PortalAssignment {
  id: string;
  vendor_id: string;
  complaint_id: string;
  sla_priority: SlaPriority;
  sla_due_at: string;
  assigned_at: string;
  resolved_at: string | null;
  resolution_notes: string | null;
  resident_rating: number | null;
  complaint: {
    id: string;
    title: string;
    category: string;
    priority: ComplaintPriority;
    status: ComplaintStatus;
    body: string;
    unit_number: string | null;
    block_name: string | null;
  };
}

interface PortalInvoice {
  id: string;
  invoice_number: string;
  amount_inr: number;
  description: string | null;
  invoice_date: string;
  paid_at: string | null;
  payment_method: string | null;
  status: InvoiceStatus;
  created_at: string;
}

interface ListResponse<T> {
  items: T[];
  next_cursor: string | null;
}

// ───── labels ─────

const CATEGORY_LABEL: Record<VendorCategory, string> = {
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  carpentry: 'Carpentry',
  ac_repair: 'AC repair',
  painting: 'Painting',
  pest_control: 'Pest control',
  housekeeping: 'Housekeeping',
  gardening: 'Gardening',
  lift_maintenance: 'Lift maintenance',
  security_system: 'Security system',
  other: 'Other'
};

const SLA_LABEL: Record<SlaPriority, string> = {
  low: 'Low · 7d',
  normal: 'Normal · 48h',
  high: 'High · 24h',
  urgent: 'Urgent · 4h'
};

const SLA_VARIANT: Record<SlaPriority, 'muted' | 'info' | 'warn' | 'alert'> = {
  low: 'muted',
  normal: 'info',
  high: 'warn',
  urgent: 'alert'
};

const INVOICE_VARIANT: Record<InvoiceStatus, 'ok' | 'warn' | 'muted'> = {
  paid: 'ok',
  unpaid: 'warn',
  cancelled: 'muted'
};

const INVOICE_LABEL: Record<InvoiceStatus, string> = {
  paid: 'Paid',
  unpaid: 'Unpaid',
  cancelled: 'Cancelled'
};

// ───── helpers ─────

function formatRupees(paise: number): string {
  return `Rs ${paise.toLocaleString('en-IN')}`;
}

/**
 * "Due in 3h" / "Due in 2d" / "Breached by 5h". Computed against now() on
 * every render — cheap because we re-render once per data refetch and once
 * per realtime event, not in a tight loop.
 */
function dueLabel(slaDueAt: string): { label: string; breached: boolean } {
  const due = new Date(slaDueAt).getTime();
  const now = Date.now();
  const diffMs = due - now;
  const absMin = Math.abs(Math.round(diffMs / 60_000));
  const fmt = (mins: number): string => {
    if (mins < 60) return `${mins}m`;
    const hr = Math.round(mins / 60);
    if (hr < 24) return `${hr}h`;
    const d = Math.round(hr / 24);
    return `${d}d`;
  };
  if (diffMs >= 0) return { label: `Due in ${fmt(absMin)}`, breached: false };
  return { label: `Breached by ${fmt(absMin)}`, breached: true };
}

const CompleteSchema = z.object({
  resolution_notes: z.string().trim().min(1, 'Add a short note').max(500)
});
type CompleteForm = z.infer<typeof CompleteSchema>;

// ───── page ─────

type Tab = 'active' | 'history' | 'invoices';

export function VendorPortalPage(): JSX.Element {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('active');
  const [activeAssignment, setActiveAssignment] = useState<PortalAssignment | null>(null);

  const profileQ = useQuery<VendorProfile>({
    queryKey: ['vendor-portal', 'me'],
    queryFn: () => get<VendorProfile>('/vendor-portal/me')
  });

  const activeQ = useQuery<ListResponse<PortalAssignment>>({
    queryKey: ['vendor-portal', 'assignments', 'open'],
    queryFn: () => get<ListResponse<PortalAssignment>>('/vendor-portal/assignments?status=open&limit=50')
  });

  const historyQ = useQuery<ListResponse<PortalAssignment>>({
    queryKey: ['vendor-portal', 'assignments', 'closed'],
    queryFn: () => get<ListResponse<PortalAssignment>>('/vendor-portal/assignments?status=closed&limit=50'),
    enabled: tab === 'history'
  });

  const invoicesQ = useQuery<ListResponse<PortalInvoice>>({
    queryKey: ['vendor-portal', 'invoices', 'all'],
    queryFn: () => get<ListResponse<PortalInvoice>>('/vendor-portal/invoices?status=all&limit=50'),
    enabled: tab === 'invoices'
  });

  const markCompleteM = useMutation({
    mutationFn: (vars: { id: string; resolution_notes: string }) =>
      post(`/vendor-portal/assignments/${vars.id}/mark-complete`, {
        resolution_notes: vars.resolution_notes
      }),
    onSuccess: () => {
      toast.success('Job marked complete');
      setActiveAssignment(null);
      void qc.invalidateQueries({ queryKey: ['vendor-portal', 'assignments'] });
      void qc.invalidateQueries({ queryKey: ['vendor-portal', 'me'] });
    },
    onError: (err) => toast.error(getErrorMessage(err))
  });

  // Realtime — a brand-new admin assignment lands → refetch open jobs.
  useEffect(() => {
    const off = subscribe('complaint:assigned', () => {
      void qc.invalidateQueries({ queryKey: ['vendor-portal', 'assignments'] });
    });
    return off;
  }, [qc]);

  const unpaidTotal = useMemo(() => {
    if (!invoicesQ.data) return null;
    return invoicesQ.data.items
      .filter((i) => i.status === 'unpaid')
      .reduce((sum, i) => sum + i.amount_inr, 0);
  }, [invoicesQ.data]);

  async function handleLogout(): Promise<void> {
    await logout();
    void navigate({ to: '/login' });
  }

  // ── render
  if (profileQ.isError) {
    return (
      <div className="flex min-h-screen items-center justify-center p-vt-6">
        <Card className="max-w-md">
          <CardContent className="flex flex-col items-center gap-vt-3 p-vt-6 text-center">
            <AlertTriangle className="h-8 w-8 text-vt-alert" />
            <h2 className="text-vt-18 font-semibold">Couldn't load your profile</h2>
            <p className="text-vt-13 text-vt-text-muted">{getErrorMessage(profileQ.error)}</p>
            <Button variant="outline" onClick={handleLogout}>
              Sign out
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-vt-bg">
      {/* ───── Top bar ───── */}
      <header className="sticky top-0 z-40 border-b border-vt-border bg-vt-surface/85 backdrop-blur supports-[backdrop-filter]:bg-vt-surface/80">
        <div className="mx-auto flex w-full max-w-[1200px] items-center justify-between gap-vt-3 px-vt-4 py-vt-3">
          <div className="flex items-center gap-vt-3">
            <LogoMark size={28} />
            <div className="flex flex-col">
              <span className="text-vt-14 font-semibold leading-tight text-vt-text">
                {profileQ.data?.name ?? user?.full_name ?? 'Vendor'}
              </span>
              {profileQ.data ? (
                <span className="flex items-center gap-vt-2 text-vt-12 text-vt-text-muted">
                  <Badge variant="info" className="capitalize">
                    {CATEGORY_LABEL[profileQ.data.category]}
                  </Badge>
                  <span>·</span>
                  <span>{profileQ.data.phone}</span>
                </span>
              ) : null}
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={handleLogout}>
            <LogOut className="h-4 w-4" />
            Sign out
          </Button>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[1200px] px-vt-4 py-vt-6">
        {/* ───── KPI strip ───── */}
        <section className="grid grid-cols-1 gap-vt-3 sm:grid-cols-3">
          <KpiCard
            icon={<Briefcase className="h-5 w-5 text-vt-info" />}
            label="Open jobs"
            value={activeQ.isLoading ? null : String(activeQ.data?.items.length ?? 0)}
          />
          <KpiCard
            icon={<CheckCircle2 className="h-5 w-5 text-vt-safe" />}
            label="Completed (all-time)"
            value={profileQ.isLoading ? null : String(profileQ.data?.jobs_completed ?? 0)}
          />
          <KpiCard
            icon={<IndianRupee className="h-5 w-5 text-vt-warn" />}
            label="Unpaid earnings"
            value={
              unpaidTotal === null
                ? tab === 'invoices'
                  ? null
                  : '—'
                : formatRupees(unpaidTotal)
            }
            hint={tab !== 'invoices' ? 'Open the Invoices tab to refresh' : undefined}
          />
        </section>

        {/* ───── Tabs ───── */}
        <div className="mt-vt-6 flex items-center gap-vt-2 border-b border-vt-border">
          <TabButton active={tab === 'active'} onClick={() => setTab('active')}>
            Active jobs
          </TabButton>
          <TabButton active={tab === 'history'} onClick={() => setTab('history')}>
            History
          </TabButton>
          <TabButton active={tab === 'invoices'} onClick={() => setTab('invoices')}>
            Invoices
          </TabButton>
        </div>

        <div className="mt-vt-4">
          {tab === 'active' ? (
            <ActiveJobsTab
              loading={activeQ.isLoading}
              items={activeQ.data?.items ?? []}
              onMarkComplete={setActiveAssignment}
            />
          ) : null}
          {tab === 'history' ? (
            <HistoryTab loading={historyQ.isLoading} items={historyQ.data?.items ?? []} />
          ) : null}
          {tab === 'invoices' ? (
            <InvoicesTab loading={invoicesQ.isLoading} items={invoicesQ.data?.items ?? []} />
          ) : null}
        </div>
      </main>

      {/* ───── Mark-complete dialog ───── */}
      <MarkCompleteDialog
        assignment={activeAssignment}
        onClose={() => setActiveAssignment(null)}
        onSubmit={(notes) => {
          if (!activeAssignment) return;
          markCompleteM.mutate({ id: activeAssignment.id, resolution_notes: notes });
        }}
        submitting={markCompleteM.isPending}
      />
    </div>
  );
}

// ───── subcomponents ─────

function KpiCard({
  icon,
  label,
  value,
  hint
}: {
  icon: React.ReactNode;
  label: string;
  value: string | null;
  hint?: string;
}): JSX.Element {
  return (
    <Card>
      <CardContent className="flex items-start gap-vt-3 p-vt-4">
        <div className="rounded-vt-sm bg-vt-surface-2 p-vt-2">{icon}</div>
        <div className="flex min-w-0 flex-col">
          <span className="text-vt-12 uppercase tracking-wider text-vt-text-muted">{label}</span>
          {value === null ? (
            <Skeleton className="mt-1 h-6 w-20" />
          ) : (
            <span className="text-vt-24 font-semibold leading-tight text-vt-text">{value}</span>
          )}
          {hint ? <span className="mt-1 text-vt-12 text-vt-text-soft">{hint}</span> : null}
        </div>
      </CardContent>
    </Card>
  );
}

function TabButton({
  active,
  onClick,
  children
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative px-vt-3 py-vt-2 text-vt-13 font-medium transition-colors duration-vt focus:outline-none focus-visible:ring-2 focus-visible:ring-vt-primary-400/30',
        active ? 'text-vt-text' : 'text-vt-text-muted hover:text-vt-text'
      )}
    >
      {children}
      <span
        className={cn(
          'absolute inset-x-2 -bottom-px h-[2px] rounded-full transition-opacity duration-vt',
          active ? 'bg-vt-primary opacity-100' : 'bg-transparent opacity-0'
        )}
      />
    </button>
  );
}

function ActiveJobsTab({
  loading,
  items,
  onMarkComplete
}: {
  loading: boolean;
  items: PortalAssignment[];
  onMarkComplete: (a: PortalAssignment) => void;
}): JSX.Element {
  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-44 w-full" />
        ))}
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <EmptyState
        icon={<Briefcase className="h-7 w-7" />}
        title="No active jobs"
        description="When a society admin assigns a complaint to you, it will appear here."
      />
    );
  }
  return (
    <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
      {items.map((a) => {
        const sla = dueLabel(a.sla_due_at);
        const location = [a.complaint.unit_number, a.complaint.block_name].filter(Boolean).join(' · ');
        return (
          <Card key={a.id} className={cn(sla.breached ? 'border-vt-alert/50' : null)}>
            <CardContent className="flex flex-col gap-vt-3 p-vt-4">
              <div className="flex items-start justify-between gap-vt-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-vt-2">
                    <Wrench className="h-4 w-4 text-vt-text-muted" />
                    <span className="text-vt-12 uppercase tracking-wider text-vt-text-muted">
                      {a.complaint.category}
                    </span>
                  </div>
                  <h3 className="mt-1 truncate text-vt-15 font-semibold text-vt-text">
                    {a.complaint.title}
                  </h3>
                  {location ? (
                    <p className="text-vt-12 text-vt-text-muted">Unit {location}</p>
                  ) : null}
                </div>
                <Badge variant={SLA_VARIANT[a.sla_priority]}>{SLA_LABEL[a.sla_priority]}</Badge>
              </div>

              <p className="line-clamp-3 text-vt-13 text-vt-text-soft">{a.complaint.body}</p>

              <div className="flex items-center justify-between gap-vt-2 border-t border-vt-border pt-vt-3">
                <div
                  className={cn(
                    'flex items-center gap-vt-1 text-vt-12 font-medium',
                    sla.breached ? 'text-vt-alert' : 'text-vt-text-muted'
                  )}
                >
                  <Clock3 className="h-3.5 w-3.5" />
                  {sla.label}
                </div>
                <Button size="sm" onClick={() => onMarkComplete(a)}>
                  Mark complete
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function HistoryTab({
  loading,
  items
}: {
  loading: boolean;
  items: PortalAssignment[];
}): JSX.Element {
  if (loading) return <Skeleton className="h-48 w-full" />;
  if (items.length === 0) {
    return (
      <EmptyState
        icon={<CheckCircle2 className="h-7 w-7" />}
        title="No completed jobs yet"
        description="Once you mark jobs complete, the history will show here."
      />
    );
  }
  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Resolved</TableHead>
            <TableHead>Job</TableHead>
            <TableHead>Unit</TableHead>
            <TableHead>Notes</TableHead>
            <TableHead className="text-right">Rating</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((a) => {
            const location = [a.complaint.unit_number, a.complaint.block_name]
              .filter(Boolean)
              .join(' · ');
            return (
              <TableRow key={a.id}>
                <TableCell className="whitespace-nowrap text-vt-13 text-vt-text-muted">
                  {formatDateTime(a.resolved_at)}
                </TableCell>
                <TableCell className="font-medium text-vt-text">{a.complaint.title}</TableCell>
                <TableCell className="text-vt-13 text-vt-text-muted">{location || '—'}</TableCell>
                <TableCell className="max-w-[280px] truncate text-vt-13 text-vt-text-soft">
                  {a.resolution_notes ?? '—'}
                </TableCell>
                <TableCell className="text-right">
                  {a.resident_rating ? (
                    <span className="inline-flex items-center gap-1 text-vt-13 text-vt-warn">
                      <Star className="h-3.5 w-3.5 fill-current" />
                      {a.resident_rating}/5
                    </span>
                  ) : (
                    <span className="text-vt-12 text-vt-text-soft">—</span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}

function InvoicesTab({
  loading,
  items
}: {
  loading: boolean;
  items: PortalInvoice[];
}): JSX.Element {
  if (loading) return <Skeleton className="h-48 w-full" />;
  if (items.length === 0) {
    return (
      <EmptyState
        icon={<Receipt className="h-7 w-7" />}
        title="No invoices yet"
        description="Once the society raises invoices for your work, they'll show up here."
      />
    );
  }
  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Invoice #</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Amount</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Paid on</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((inv) => (
            <TableRow key={inv.id}>
              <TableCell className="font-mono text-vt-13">{inv.invoice_number}</TableCell>
              <TableCell className="text-vt-13 text-vt-text-muted">{formatDate(inv.invoice_date)}</TableCell>
              <TableCell className="font-semibold text-vt-text">{formatRupees(inv.amount_inr)}</TableCell>
              <TableCell>
                <Badge variant={INVOICE_VARIANT[inv.status]}>{INVOICE_LABEL[inv.status]}</Badge>
              </TableCell>
              <TableCell className="text-vt-13 text-vt-text-muted">
                {inv.paid_at ? formatDate(inv.paid_at) : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function MarkCompleteDialog({
  assignment,
  onClose,
  onSubmit,
  submitting
}: {
  assignment: PortalAssignment | null;
  onClose: () => void;
  onSubmit: (notes: string) => void;
  submitting: boolean;
}): JSX.Element {
  const form = useForm<CompleteForm>({
    resolver: zodResolver(CompleteSchema),
    defaultValues: { resolution_notes: '' }
  });

  // Reset form when a fresh assignment is selected so notes don't bleed
  // across submissions.
  useEffect(() => {
    if (assignment) form.reset({ resolution_notes: '' });
  }, [assignment, form]);

  const open = assignment !== null;

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark complete</DialogTitle>
          <DialogDescription>
            {assignment ? assignment.complaint.title : ''}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit((v) => onSubmit(v.resolution_notes))}
          className="flex flex-col gap-vt-3"
        >
          <div className="flex flex-col gap-vt-2">
            <Label htmlFor="resolution_notes">Resolution notes</Label>
            <Textarea
              id="resolution_notes"
              placeholder="Describe the fix (parts, time, observations)…"
              rows={4}
              autoFocus
              {...form.register('resolution_notes')}
            />
            {form.formState.errors.resolution_notes ? (
              <p className="text-vt-12 text-vt-alert" role="alert">
                {form.formState.errors.resolution_notes.message}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Mark complete'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
